import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, X, ShieldCheck, ExternalLink, Link2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminCommunityPage = () => {
  const { user, loading, isAdmin, login } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ applications: [], counts: {} });
  const [busy, setBusy] = useState(null);
  const [filter, setFilter] = useState('pending');
  const [invites, setInvites] = useState([]);
  const [inviteText, setInviteText] = useState('');
  const [inviteTitle, setInviteTitle] = useState('');
  const [inviteCompany, setInviteCompany] = useState('');
  const [inviteMsg, setInviteMsg] = useState('');
  const [inviteBusy, setInviteBusy] = useState(false);
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [copied, setCopied] = useState(null);
  const [stats, setStats] = useState(null);

  const load = useCallback(async () => {
    try {
      const [aRes, iRes, sRes] = await Promise.all([
        fetch(`${API}/api/admin/applications`, { credentials: 'include' }),
        fetch(`${API}/api/admin/invites`, { credentials: 'include' }),
        fetch(`${API}/api/admin/stats`, { credentials: 'include' }),
      ]);
      if (aRes.ok) setData(await aRes.json());
      if (iRes.ok) { const j = await iRes.json(); setInvites(j.invites || []); if (typeof j.email_enabled === 'boolean') setEmailEnabled(j.email_enabled); }
      if (sRes.ok) setStats(await sRes.json());
    } catch (_) { /* ignore */ }
  }, []);

  const sendInvites = async () => {
    if (!inviteText.trim()) return;
    setInviteBusy(true); setInviteMsg('');
    try {
      const res = await fetch(`${API}/api/admin/invite`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emails: inviteText, title: inviteTitle || null, company: inviteCompany || null }),
      });
      if (res.ok) {
        const d = await res.json();
        setEmailEnabled(d.email_enabled);
        if (d.email_enabled) {
          setInviteMsg(`Invited ${d.added} person(s) — ${d.emails_sent} invite email(s) sent. They're pre-approved and become members on Google login.`);
        } else {
          setInviteMsg(`Pre-approved ${d.added} email(s). They become members automatically on Google login. (Invite emails are OFF — add a Resend API key to send them.)`);
        }
        setInviteText(''); setInviteTitle(''); setInviteCompany(''); await load();
      }
    } catch (_) { setInviteMsg('Something went wrong.'); }
    setInviteBusy(false);
  };

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  const decide = async (uid, decision) => {
    setBusy(uid + decision);
    try {
      await fetch(`${API}/api/admin/applications/${uid}/${decision}`, { method: 'POST', credentials: 'include' });
      await load();
    } catch (_) { /* ignore */ }
    setBusy(null);
  };

  const apps = (data.applications || []).filter((a) => filter === 'all' || a.status === filter);

  return (
    <div className="min-h-screen bg-white">
      <SEO title="Admin · Community review" description="Review membership applications." path="/admin/community" noindex />
      <Header />
      <main className="pt-20 pb-24">
        <div className="max-w-4xl mx-auto px-6">
          <button onClick={() => navigate('/community')} className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-8"><ArrowLeft className="w-4 h-4" /> Community</button>

          {loading ? (
            <div className="text-slate-400 text-sm">Loading…</div>
          ) : !user ? (
            <div className="text-center py-10">
              <ShieldCheck className="w-10 h-10 text-slate-400 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-slate-900">Admin access</h1>
              <p className="text-slate-600 mt-2">Sign in with an admin account to review applications.</p>
              <button onClick={login} className="mt-6 px-6 py-3 rounded-xl bg-slate-900 text-white font-semibold">Continue with Google</button>
            </div>
          ) : !isAdmin ? (
            <div className="text-center py-10" data-testid="admin-denied">
              <ShieldCheck className="w-10 h-10 text-slate-400 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-slate-900">Not an admin</h1>
              <p className="text-slate-600 mt-2">Your account ({user.email}) is not on the admin list. Add it to <code className="bg-slate-100 px-1.5 py-0.5 rounded text-sm">ADMIN_EMAILS</code> in the backend.</p>
            </div>
          ) : (
            <>
              <div className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500 mb-2">Admin</div>
              <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Membership review</h1>

              {/* Stats overview */}
              {stats && (
                <div className="mt-6" data-testid="admin-stats">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <StatCard label="Signed up" value={stats.signed_up} sub={`+${stats.signups_7d} this week`} tone="slate" />
                    <StatCard label="Vetted members" value={stats.members} sub={`${stats.admins} admin${stats.admins === 1 ? '' : 's'}`} tone="emerald" />
                    <StatCard label="Pending review" value={stats.applications.pending} sub={`${stats.applications.approved} approved · ${stats.applications.rejected} rejected`} tone="amber" />
                    <StatCard label="People messaging" value={stats.messaging.people_messaging} sub={`${stats.messaging.conversations} conversations`} tone="indigo" />
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                    <StatCard label="Messages sent" value={stats.messaging.messages_total} sub={`${stats.messaging.messages_7d} this week`} tone="slate" />
                    <StatCard label="Applications" value={stats.applications.total} sub="all time" tone="slate" />
                    <StatCard label="Pre-approved" value={stats.invited.total} sub={`${stats.invited.joined} joined`} tone="slate" />
                    <StatCard label="Approval rate" value={stats.applications.total ? `${Math.round((stats.applications.approved / stats.applications.total) * 100)}%` : '—'} sub="of applications" tone="slate" />
                  </div>
                  {stats.recent_signups?.length > 0 && (
                    <div className="mt-4 bg-white border border-slate-200 rounded-2xl p-5" data-testid="admin-recent">
                      <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500 mb-3">Recent sign-ups</div>
                      <div className="flex flex-col divide-y divide-slate-100">
                        {stats.recent_signups.map((u) => (
                          <div key={u.email} className="flex items-center gap-3 py-2.5">
                            {u.picture ? <img src={u.picture} alt="" className="w-8 h-8 rounded-full object-cover" /> : <div className="w-8 h-8 rounded-full bg-slate-200" />}
                            <div className="min-w-0 flex-1"><span className="text-sm font-semibold text-slate-900">{u.name || u.email}</span> <span className="text-xs text-slate-400">· {u.email}</span></div>
                            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${u.role === 'admin' ? 'bg-purple-50 text-purple-700' : u.status === 'approved' ? 'bg-emerald-50 text-emerald-700' : u.status === 'pending' ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>{u.role === 'admin' ? 'admin' : u.status}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Pre-approve / invite existing leaders */}
              <div className="mt-6 bg-slate-900 text-white rounded-2xl p-6" data-testid="admin-invite-panel">
                <div className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400 mb-2">Pre-approve members</div>
                <h2 className="text-lg font-bold">Invite existing leaders by email</h2>
                <p className="text-sm text-slate-300 mt-1 mb-4 leading-relaxed">Paste the Google emails of people you want in. Each person gets an <strong className="text-white">invite email</strong> from Forsyt, and when they log in with Google they are auto-approved as members — no application needed. Optional role/company apply to this batch.</p>
                {!emailEnabled && (
                  <div className="mb-4 text-xs text-amber-200 bg-amber-500/10 border border-amber-400/30 rounded-lg px-3 py-2" data-testid="admin-email-warning">
                    Invite emails are currently OFF. Add a <code className="bg-black/30 px-1 rounded">RESEND_API_KEY</code> in backend env to start sending them. Emails will still be pre-approved.
                  </div>
                )}
                <textarea value={inviteText} onChange={(e) => setInviteText(e.target.value)} rows={3} placeholder="jane@company.com, ravi@startup.io, …" data-testid="admin-invite-emails"
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white text-sm placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-white/20" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                  <input value={inviteTitle} onChange={(e) => setInviteTitle(e.target.value)} placeholder="Title (optional, e.g. CRO)" data-testid="admin-invite-title" className="px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-white text-sm placeholder:text-slate-500 focus:outline-none" />
                  <input value={inviteCompany} onChange={(e) => setInviteCompany(e.target.value)} placeholder="Company (optional)" data-testid="admin-invite-company" className="px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-white text-sm placeholder:text-slate-500 focus:outline-none" />
                </div>
                <div className="flex items-center gap-3 mt-4">
                  <button onClick={sendInvites} disabled={inviteBusy} data-testid="admin-invite-submit" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white text-slate-900 text-sm font-semibold hover:bg-slate-100 disabled:opacity-60">{inviteBusy ? 'Sending…' : 'Send invites'}</button>
                  {inviteMsg && <span className="text-sm text-emerald-300" data-testid="admin-invite-msg">{inviteMsg}</span>}
                </div>
                {invites.length > 0 && (
                  <div className="mt-5 pt-4 border-t border-slate-800">
                    <div className="text-xs text-slate-400 mb-3">{invites.length} invited · copy a link and send it to them directly</div>
                    <div className="space-y-2" data-testid="admin-invite-list">
                      {invites.map((inv) => {
                        const link = inv.token ? `${window.location.origin}/community/invite/${inv.token}` : '';
                        return (
                          <div key={inv.email} className="flex items-center gap-2 bg-slate-800/60 border border-slate-700 rounded-lg px-3 py-2" data-testid={`admin-invite-row-${inv.email}`}>
                            <span className="text-sm text-slate-200 truncate flex-1">{inv.email}</span>
                            <span className={`text-[10px] px-2 py-0.5 rounded-full shrink-0 ${inv.joined ? 'bg-emerald-500/15 text-emerald-300' : inv.emailed_at ? 'bg-sky-500/15 text-sky-300' : 'bg-slate-700 text-slate-300'}`}>
                              {inv.joined ? 'joined' : inv.emailed_at ? 'emailed' : 'pending'}
                            </span>
                            {link && (
                              <button
                                onClick={() => { navigator.clipboard?.writeText(link); setCopied(inv.email); setTimeout(() => setCopied(null), 1600); }}
                                data-testid={`admin-copy-link-${inv.email}`}
                                className="shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-md bg-white text-slate-900 hover:bg-slate-100"
                              >
                                {copied === inv.email ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Link2 className="w-3.5 h-3.5" /> Copy link</>}
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-2 mt-8 mb-6" data-testid="admin-filters">
                {['pending', 'approved', 'rejected', 'all'].map((f) => (
                  <button key={f} onClick={() => setFilter(f)} className={`px-4 py-2 rounded-full text-sm font-medium border capitalize ${filter === f ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200'}`}>
                    {f}{f !== 'all' && data.counts?.[f] != null ? ` (${data.counts[f]})` : ''}
                  </button>
                ))}
              </div>

              {apps.length === 0 ? (
                <div className="text-center py-16 border border-dashed border-slate-200 rounded-2xl text-slate-500" data-testid="admin-empty">No {filter} applications.</div>
              ) : (
                <div className="space-y-3" data-testid="admin-applications">
                  {apps.map((a) => (
                    <div key={a.user_id} data-testid={`admin-app-${a.user_id}`} className="bg-white border border-slate-200 rounded-2xl p-5 flex flex-col md:flex-row md:items-center gap-4">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        {a.picture ? <img src={a.picture} alt="" className="w-11 h-11 rounded-full" /> : <div className="w-11 h-11 rounded-full bg-slate-200" />}
                        <div className="min-w-0">
                          <div className="font-bold text-slate-900 truncate">{a.name} <span className="text-xs font-normal text-slate-400">· {a.email}</span></div>
                          <div className="text-sm text-slate-600 truncate">{a.title || '—'} · {a.company || '—'} {a.city ? `· ${a.city}` : ''}</div>
                          {a.reason && <div className="text-xs text-slate-500 mt-1 line-clamp-2">"{a.reason}"</div>}
                          {a.linkedin && <a href={a.linkedin.startsWith('http') ? a.linkedin : `https://${a.linkedin}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-indigo-600 mt-1">LinkedIn <ExternalLink className="w-3 h-3" /></a>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-full ${a.status === 'approved' ? 'bg-emerald-50 text-emerald-700' : a.status === 'rejected' ? 'bg-red-50 text-red-600' : 'bg-amber-50 text-amber-700'}`}>{a.status}</span>
                        {a.status !== 'approved' && (
                          <button onClick={() => decide(a.user_id, 'approve')} disabled={busy} data-testid={`admin-approve-${a.user_id}`} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 disabled:opacity-50"><Check className="w-3.5 h-3.5" /> Approve</button>
                        )}
                        {a.status !== 'rejected' && (
                          <button onClick={() => decide(a.user_id, 'reject')} disabled={busy} data-testid={`admin-reject-${a.user_id}`} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-slate-100 text-slate-600 text-xs font-semibold hover:bg-slate-200 disabled:opacity-50"><X className="w-3.5 h-3.5" /> Reject</button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

const TONES = {
  slate: 'text-slate-900',
  emerald: 'text-emerald-600',
  amber: 'text-amber-600',
  indigo: 'text-indigo-600',
};
const StatCard = ({ label, value, sub, tone }) => (
  <div className="bg-white border border-slate-200 rounded-2xl p-5">
    <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400">{label}</div>
    <div className={`text-3xl font-extrabold tracking-tight mt-1.5 ${TONES[tone] || TONES.slate}`}>{value}</div>
    {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
  </div>
);

export default AdminCommunityPage;
